import React, { useCallback, useId, useState } from 'react';
import { Alert, Button, Card, InputNumber, Select, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { postProjectionSimulate } from '../../services/api/supplySnapshotService';
import type { ProjectionTrajectoryPoint } from '../../types/clientSnapshotReports';
import { getApiErrorMessage } from '../../utils/apiErrors';
import styles from './ProjectionSimulatePanel.module.css';

const { Text, Paragraph } = Typography;

const SCENARIO_OPTIONS = [
  { value: 'baseline', label: 'Baseline' },
  { value: 'optimistic', label: 'Optimistic' },
  { value: 'pessimistic', label: 'Pessimistic / stress' },
  { value: 'flat', label: 'Flat / steady' },
];

export interface ProjectionSimulatePanelProps {
  /** Workspace company UUID (POST /projection/simulate). */
  companyId: string | null | undefined;
}

/**
 * ISSUE-218 / ISSUE-222: runs WP-010 projection simulate and shows trajectory (supply §5.2).
 */
export const ProjectionSimulatePanel: React.FC<ProjectionSimulatePanelProps> = ({ companyId }) => {
  const scenarioId = useId();
  const monthsId = useId();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [points, setPoints] = useState<ProjectionTrajectoryPoint[]>([]);
  const [meta, setMeta] = useState<{
    scenario: string;
    months: number;
    stub?: boolean;
  } | null>(null);
  const [scenario, setScenario] = useState('baseline');
  const [months, setMonths] = useState(6);

  const runSim = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await postProjectionSimulate({
        company_id: companyId,
        scenario,
        months,
      });
      setPoints(res.trajectory ?? []);
      setMeta({
        scenario: res.scenario,
        months: res.months,
        stub: res.stub,
      });
    } catch (e: unknown) {
      setPoints([]);
      setMeta(null);
      setError(getApiErrorMessage(e, 'Projection simulate failed.'));
    } finally {
      setLoading(false);
    }
  }, [companyId, scenario, months]);

  const columns: ColumnsType<ProjectionTrajectoryPoint> = [
    { title: 'Month', dataIndex: 'month_index', key: 'month_index', width: 72 },
    {
      title: 'Entropy',
      dataIndex: 'entropy',
      key: 'entropy',
      render: (v: unknown) => (typeof v === 'number' ? v.toFixed(4) : '—'),
    },
    {
      title: 'Index proxy',
      dataIndex: 'systemic_index_proxy',
      key: 'systemic_index_proxy',
      render: (v: unknown) => (typeof v === 'number' ? v.toFixed(2) : '—'),
    },
    {
      title: 'Note',
      dataIndex: 'note',
      key: 'note',
      ellipsis: true,
    },
  ];

  if (!companyId) {
    return (
      <Card title="Supply projection (simulate)">
        <Alert type="warning" showIcon message="Select a workspace company to run projection simulate." />
      </Card>
    );
  }

  return (
    <Card title="Supply projection (simulate)">
      <Paragraph type="secondary" style={{ marginBottom: 12 }}>
        Deterministic trajectory from value-metrics systemic index (backend WP-010). Scoped to your
        workspace company (not the product artifact id on this dashboard). Product charts
        above use the selected product.
      </Paragraph>
      {error && <Alert type="error" showIcon style={{ marginBottom: 12 }} message={error} />}
      <div className={styles.toolbar} role="group" aria-label="Projection simulation controls">
        <div className={styles.field}>
          <label className={styles.label} htmlFor={scenarioId}>
            Scenario
          </label>
          <Select
            id={scenarioId}
            style={{ minWidth: 200 }}
            value={scenario}
            onChange={setScenario}
            options={SCENARIO_OPTIONS}
          />
        </div>
        <div className={styles.field}>
          <label className={styles.label} htmlFor={monthsId}>
            Months
          </label>
          <InputNumber
            id={monthsId}
            min={1}
            max={60}
            value={months}
            onChange={(v) => setMonths(v ?? 6)}
          />
        </div>
        <Button type="primary" loading={loading} onClick={() => void runSim()}>
          Run simulation
        </Button>
        {meta && (
          <Space wrap>
            <Text type="secondary">
              Last run: {meta.scenario}, {meta.months} months
            </Text>
            {meta.stub === true && <Tag color="orange">stub</Tag>}
          </Space>
        )}
      </div>
      {points.length > 0 && (
        <Table<ProjectionTrajectoryPoint>
          style={{ marginTop: 16 }}
          size="small"
          rowKey={(r) => String(r.month_index)}
          columns={columns}
          dataSource={points}
          pagination={false}
        />
      )}
    </Card>
  );
};
